import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Actions from "./components/Actions";
import HeaderComponent from "../../../../components/ui/ListHeader";
import searchedJobs from "./components/SearchedJobs";
import * as SelectListWorking from "../../components/SelectListWorking";
import * as SelectListSaved from "../../components/SelectListSaved";
import {
  jobFilterStore,
  jobNavigationStore,
} from "../../components/useJobsFilters";
import {
  Table,
  multiSelect,
  SelectList,
} from "../../../../components/Functional";
import JobStatus from "./components/JobStatus";
import { FETCH_LISTS as fetchLists } from "../../../../graphql/query/List";
import { CREATE_LIST as createListMutation } from "../../../../graphql/mutation/List";
import { LIST_TYPES } from "../../../../constants/listTypes";
import { updateSearchResultNavigationStore } from "../../../../util/searchResultNavigation";
import JobIdLink from "./components/JobIdLink";

const List = props => {
  const { formik } = props;
  const navigate = useNavigate();
  const {
    data: { lists: { edges = [] } = {} } = {},
    loading: loadingWorkingList,
  } = useQuery(fetchLists(), {
    variables: {
      input: { typeId: LIST_TYPES.JOB_WORKING },
    },
    fetchPolicy: "network-only",
  });

  const [createList] = useMutation(createListMutation(), {
    refetchQueries: [
      {
        query: fetchLists(),
        variables: { input: { typeId: LIST_TYPES.JOB_WORKING } },
        fetchPolicy: "network-only",
      },
    ],
    awaitRefetchQueries: true,
  });

  // Get the first list's ID if it exists (even if empty, the list object will have an id)
  const workingListId = edges?.[0]?.id ?? null;

  // Ensure working list exists - create it if it doesn't
  useEffect(() => {
    if (!loadingWorkingList && !workingListId) {
      createList({
        variables: {
          input: {
            typeId: LIST_TYPES.JOB_WORKING,
            name: `select_list_working_${Date.now()}`,
          },
        },
      });
    }
  }, [loadingWorkingList, workingListId, createList]);

  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams?.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const listId = listIdParam ? +listIdParam : null;
  const ids = Object.keys(formik?.values?.selected?.ids || {})?.map(item =>
    parseInt(item.replace("_", "")),
  );

  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    let shouldUpdate = false;

    // Remove workingListId if we're viewing a saved list (listId is present)
    if (listId && currentParams.has("workingListId")) {
      currentParams.delete("workingListId");
      shouldUpdate = true;
    }

    // Update workingListId if we're on selectListWorking tab, it's available, and no saved listId
    if (status === "selectListWorking" && workingListId && !listId) {
      if (currentParams.get("workingListId") !== String(workingListId)) {
        currentParams.set("workingListId", workingListId);
        shouldUpdate = true;
      }
    }

    if (shouldUpdate) {
      const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
      // eslint-disable-next-line
      history.pushState(null, null, newUrl);
    }
  }, [workingListId, status, listId]);

  const multiSelectProps = {
    formik,
    label: "Jobs",
    name: "selected",
    keyName: "id",
  };

  const actionProps = {
    formik,
    ids,
    listId,
    onSuccess: () => formik?.resetForm(),
    search: jobFilterStore.get(),
    workingListId,
  };

  const captureNavigationSnapshot = useCallback(
    ({ count, edges, queryVariables }) => {
      if (status !== "all") return;

      const currentQueryParams = new URLSearchParams(window.location.search);
      const currentListId = currentQueryParams.get("listId");
      const currentWorkingListId = currentQueryParams.get("workingListId");

      updateSearchResultNavigationStore(jobNavigationStore, {
        count,
        edges,
        queryVariables,
        statusContext: "all",
        listContext: {
          ...(currentListId ? { listId: currentListId } : {}),
          ...(currentWorkingListId
            ? { workingListId: currentWorkingListId }
            : {}),
        },
      });
    },
    [status],
  );

  const handleChange = value => {
    formik?.resetForm();
    let url = `/jobs?status=${value}`;

    // For working list tabs, use listId if present (saved list), otherwise use workingListId
    if (value === "selectListWorking" || value === "selectListSaved") {
      // Include listId if present (viewing/editing a saved list), otherwise use workingListId
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
    } else {
      // For "all" and other tabs, preserve saved list IDs (listId) so items can be added to them
      // But don't include working list IDs
      if (listId) {
        url += `&listId=${listId}`;
      }
    }

    navigate(url);
  };

  const GeneralTabActions = () => {
    // Determine target list ID - use saved list ID if present, otherwise use working list ID
    const targetListId =
      listId && !isNaN(listId) ? listId : workingListId || undefined;

    const createListProps = {
      ...actionProps,
      // Use listId if present (saved list), otherwise use workingListId
      ...(targetListId ? { targetListId } : {}),
      onSuccess: () => {
        formik?.resetForm();
        navigate(
          `/jobs?status=selectListWorking${listId ? `&listId=${listId}` : ""}`,
        );
      },
      typeId: 18, // JOB_WORKING - not yet in LIST_TYPES constants
    };

    return <SelectList.CreateList {...createListProps} />;
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      {
        actions: GeneralTabActions,
        label: "All Jobs",
        name: "all",
        tableProps: searchedJobs(),
      },
      {
        actions: SelectListWorking.Actions,
        label: "Select List (Working)",
        name: "selectListWorking",
        tableProps: SelectListWorking.tableProps(actionProps),
      },
      {
        actions: SelectListSaved.Actions,
        label: "Select List (Saved)",
        name: "selectListSaved",
        tableProps: SelectListSaved.tableProps({ formik }),
      },
    ],
  };
  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};

  const fixHeadersName = ({ status, headers }) =>
    headers?.map(header => {
      return {
        ...header,
        name:
          header?.name && status === "selectListWorking"
            ? Array.isArray(header?.name)
              ? header?.name?.map(item => `item.${item}`)
              : `item.${header?.name}`
            : header?.name,
      };
    });

  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "jobs",
    headers: fixHeadersName({
      status,
      headers: [
        multiSelect(multiSelectProps),
        {
          label: "Job",
          name: "id",
          type: "custom",
          component: JobIdLink,
        },
        {
          label: "Date",
          name: "date",
          type: "date",
        },
        {
          label: "Job Status",
          name: "jobStatus.description",
          type: "custom",
          component: JobStatus,
        },
        {
          label: "Pickup Group",
          name: "pickupGroup.name",
          type: "label",
        },
        {
          label: "Type",
          name: "jobType.description",
          type: "label",
        },
        {
          label: "Due Date",
          name: "dueDate",
          type: "date",
        },
        { label: "Due Time", name: "duetime", type: "time" },
        {
          label: "Description",
          name: "description",
          type: "label",
        },
        { label: "", name: "action", type: "custom", component: Actions },
      ],
    }),
    // inputs: [
    //   { label: 'Artist name', name: 'artistName', type: 'text', view: 'partial' },
    //   { label: 'Status', name: 'statusId', type: 'multi-select', view: 'partial', options: instanceStatus?.map(item => ({ label: item.name, value: item.id })) },
    // ],
    limit: 50,
    onQueryStateChange: captureNavigationSnapshot,
    tabProps,
    pollInterval: 10000,
    ...tabTableProps,
  };

  return (
    <>
      <HeaderComponent
        leftText="Job Requests"
        rightComponent={() => {
          return <div className="flex gap-4">{tabActions?.(actionProps)}</div>;
        }}
      />
      <div className="mt-6 w-full">
        <Table {...tableProps} />
      </div>
    </>
  );
};

export default List;
